import React, { useState } from 'react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'

interface DropdownProps {
  isOpen?: boolean
  toggle?: () => void
  children: React.ReactNode
  className?: string
  direction?: string
}

export const Dropdown: React.FC<DropdownProps> = ({ isOpen, toggle, children, className }) => {
  return (
    <DropdownMenu open={isOpen} onOpenChange={(open) => { if (toggle && open !== isOpen) toggle() }}>
      <div className={cn(className)}>{children}</div>
    </DropdownMenu>
  )
}

export const UncontrolledDropdown: React.FC<Omit<DropdownProps, 'isOpen' | 'toggle'>> = ({
  children,
  className,
}) => {
  return (
    <DropdownMenu>
      <div className={cn(className)}>{children}</div>
    </DropdownMenu>
  )
}

interface DropdownToggleProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  caret?: boolean
  color?: string
  size?: string
  nav?: boolean
  tag?: React.ElementType
}

export const DropdownToggle = React.forwardRef<HTMLButtonElement, DropdownToggleProps>(
  ({ children, className, nav, ...props }, ref) => (
    <DropdownMenuTrigger asChild ref={ref}>
      <button className={cn(nav && 'inline-flex items-center gap-2 bg-transparent border-none cursor-pointer p-0 text-sm', className)} {...props}>
        {children}
      </button>
    </DropdownMenuTrigger>
  ),
)
DropdownToggle.displayName = 'DropdownToggle'

interface DropdownMenuProps {
  right?: boolean
  children: React.ReactNode
  className?: string
}

export const DropdownMenuCompat: React.FC<DropdownMenuProps> = ({ right, children, className }) => (
  <DropdownMenuContent align={right ? 'end' : 'start'} className={cn(className)}>
    {children}
  </DropdownMenuContent>
)

// Re-export with Reactstrap name
export { DropdownMenuCompat as DropdownMenu_Compat }

interface DropdownItemProps extends React.HTMLAttributes<HTMLDivElement> {
  header?: boolean
  divider?: boolean
  tag?: React.ElementType
  href?: string
  toggle?: boolean
}

export const DropdownItem: React.FC<DropdownItemProps> = ({
  header,
  divider,
  children,
  className,
  ...props
}) => {
  if (divider) return <DropdownMenuSeparator />
  if (header) return <div className="px-2 py-1.5 text-sm font-semibold">{children}</div>

  return (
    <DropdownMenuItem className={cn(className)} {...props}>
      {children}
    </DropdownMenuItem>
  )
}
